import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { KeyRound, ArrowLeft } from "lucide-react";

export default function PasswordReset() {
  const { t } = useI18n();
  const [step, setStep] = useState("request"); // request | confirm
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [devToken, setDevToken] = useState("");
  const nav = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    const urlToken = params.get("token");
    if (urlToken) { setToken(urlToken); setStep("confirm"); }
  }, [params]);

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-reset/request", { email, origin_url: window.location.origin });
      toast.success(data.message);
      if (data.dev_token) { setDevToken(data.dev_token); setToken(data.dev_token); }
      setStep("confirm");
    } catch (err) { toast.error(err.response?.data?.detail || t("generic_error")); }
    finally { setBusy(false); }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) { toast.error(t("reset_min_chars")); return; }
    setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: newPwd });
      toast.success(t("reset_updated"));
      nav("/auth");
    } catch (err) { toast.error(err.response?.data?.detail || t("generic_error")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-6" data-testid="password-reset-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <Link to="/auth" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="reset-back-link">
          <ArrowLeft size={14} /> {t("reset_back_to_login")}
        </Link>
        <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center mb-4"><KeyRound size={22} /></div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">{t("reset_title")}</h1>
        {step === "request" ? (
          <>
            <p className="mt-2 text-sm text-slate-600">{t("reset_request_desc")}</p>
            <form onSubmit={requestReset} className="mt-6 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("email")}</Label>
                <Input data-testid="reset-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
              </div>
              <Button data-testid="reset-request-btn" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white font-semibold">
                {busy ? "..." : t("reset_send_link")}
              </Button>
            </form>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm text-slate-600">{t("reset_confirm_desc")}</p>
            {devToken && (
              <div className="mt-3 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-900" data-testid="dev-token-notice">
                <b>{t("reset_dev_notice_b")}</b> {t("reset_dev_notice")}
              </div>
            )}
            <form onSubmit={confirmReset} className="mt-6 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("token_label")}</Label>
                <Input data-testid="reset-token" required value={token} onChange={(e) => setToken(e.target.value)} className="mt-1.5 h-11 rounded-xl font-mono text-xs" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("new_password")}</Label>
                <PasswordInput testId="reset-new-password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required minLength={6} className="mt-1.5 h-11 rounded-xl" />
              </div>
              <Button data-testid="reset-confirm-btn" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white font-semibold">
                {busy ? "..." : t("reset_update_pwd")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
